import axios from 'axios';
import type { AxiosResponse } from 'axios';
import { API_CONFIG, SESSION_KEYS } from '@/config/constants';
import { features } from '@/config/features';
import { clearAuthSession } from '@/utils/session';

// Extend AxiosRequestConfig to include our metadata
declare module 'axios' {
  interface InternalAxiosRequestConfig {
    metadata?: {
      startTime: number;
      requestId: string;
      suppressErrorLog?: boolean;
    };
  }
}

// Create the base API client
export const apiClient = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for authentication and debug logging
apiClient.interceptors.request.use(
  (config) => {
    // Add auth token from localStorage (persists across tabs)
    const token = localStorage.getItem(SESSION_KEYS.AUTH_TOKEN);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Development-only debug logging
    if (import.meta.env.DEV) {
      const requestId = crypto.randomUUID();
      config.metadata = { startTime: Date.now(), requestId };
      
      if (features.verboseApiLogging) {
        console.log('[API Request]', {
          id: requestId,
          method: config.method?.toUpperCase(),
          url: config.url,
          data: config.data,
          params: config.params,
        });
      }
    }
    
    return config;
  },
  (error) => {
    if (import.meta.env.DEV && features.verboseApiLogging) {
      console.error('[API Request Error]', error);
    }
    return Promise.reject(error);
  }
);

// Response interceptor for auth handling and debug logging
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // Development-only debug logging
    if (import.meta.env.DEV && response.config.metadata) {
      const duration = Date.now() - response.config.metadata.startTime;
      
      if (features.verboseApiLogging) {
        console.log('[API Response]', {
          id: response.config.metadata.requestId,
          status: response.status,
          duration: `${duration}ms`,
          url: response.config.url,
          data: response.data,
        });
      }
      
      // Record to debug store if available
      if (window.__DEBUG_STORE__) {
        window.__DEBUG_STORE__.addApiLog({
          id: response.config.metadata!.requestId,
          timestamp: response.config.metadata!.startTime,
          method: response.config.method!.toUpperCase(),
          url: response.config.url!,
          status: response.status,
          duration,
          request: response.config.data,
          response: response.data,
        });
      }
    }
    
    return response;
  },
  (error) => {
    // Silently ignore canceled requests (from AbortController cleanup)
    // These are intentional cancellations when components unmount
    if (axios.isCancel(error) || error.message === 'canceled') {
      return Promise.reject(error);
    }

    // Handle 401 Unauthorized
    if (error.response?.status === 401) {
      clearAuthSession();

      // Only redirect to login if not already on the login page
      // This prevents page reload on failed login attempts
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }

    // Development-only error logging
    if (import.meta.env.DEV) {
      const suppressErrorLog = error.config?.metadata?.suppressErrorLog || false;

      if (features.verboseApiLogging && !suppressErrorLog) {
        console.error('[API Error]', {
          status: error.response?.status,
          message: error.message,
          data: error.response?.data,
          config: error.config,
        });
      }

      // Record error to debug store if available
      if (window.__DEBUG_STORE__ && error.config?.metadata) {
        const duration = Date.now() - error.config.metadata.startTime;
        window.__DEBUG_STORE__.addApiLog({
          id: error.config.metadata.requestId,
          timestamp: error.config.metadata.startTime,
          method: error.config.method?.toUpperCase() || 'UNKNOWN',
          url: error.config.url || 'unknown',
          status: error.response?.status || 0,
          duration,
          request: error.config.data,
          response: null,
          error: {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status
          },
        });
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;